import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

const VehicleInspectionUpload = ({ onFilesChange, submissionProgress }) => {
  const [selectedDocType, setSelectedDocType] = useState('');
  const [customDocType, setCustomDocType] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const documentTypes = ['Invoice', 'Packing List', 'Delivery Challan', 'EWAY Bill', 'Inspection Report', 'MDCC', 'LR copy', 'Others'];

  const handleDocTypeChange = (e) => {
    const value = e.target.value;
    setSelectedDocType(value);
    if (value !== 'Others') {
      setCustomDocType('');
    }
  };

  const handleCustomDocTypeChange = (e) => {
    setCustomDocType(e.target.value);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const effectiveDocType = selectedDocType === 'Others' ? customDocType : selectedDocType;
    if (!effectiveDocType) {
      alert('Please select or enter a document type.');
      return;
    }
    if (files.length === 0) {
      alert('Please select at least one file.');
      return;
    }

    const newFiles = {
      type: effectiveDocType,
      files: files.map(file => ({
        id: uuidv4(),
        file,
        name: file.name,
      })),
      uploadedAt: new Date().toISOString(),
    };
    const updatedFiles = [...uploadedFiles, newFiles];
    setUploadedFiles(updatedFiles);
    onFilesChange(updatedFiles);
    e.target.value = '';
  };

  const removeFile = (docType, fileId) => {
    const updatedFiles = uploadedFiles
      .map((group) => {
        if (group.type === docType) {
          return {
            ...group,
            files: group.files.filter((f) => f.id !== fileId),
          };
        }
        return group;
      })
      .filter((group) => group.files.length > 0); // Remove empty document type groups

    setUploadedFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const groupedFiles = uploadedFiles.reduce((acc, file) => {
    acc[file.type] = acc[file.type] || [];
    acc[file.type].push(...file.files.map((f) => ({
      id: f.id,
      name: f.name,
      uploadedAt: file.uploadedAt,
    })));
    return acc;
  }, {});

  return (
    <div className="mb-8">
      <h2 className="text-xl font-semibold text-gray-700 mb-4">Document Upload</h2>
      <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-600 mb-1">Document Type</label>
            <select
              value={selectedDocType}
              onChange={handleDocTypeChange}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition bg-white"
            >
              <option value="">Select Document Type</option>
              {documentTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          {selectedDocType === 'Others' && (
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-600 mb-1">Custom Document Type</label>
              <input
                type="text"
                value={customDocType}
                onChange={handleCustomDocTypeChange}
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition"
                placeholder="Enter custom document type"
              />
            </div>
          )}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-600 mb-1">Upload Files</label>
            <input
              type="file"
              multiple
              onChange={handleFileUpload}
              disabled={!selectedDocType || (selectedDocType === 'Others' && !customDocType)}
              className="w-full p-1 border rounded-lg text-gray-600 bg-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-white hover:file:bg-gray-700 transition"
            />
          </div>
        </div>
        {Object.keys(groupedFiles).length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-700 mb-4">Uploaded Documents</h3>
            {Object.entries(groupedFiles).map(([docType, files]) => (
              <div key={docType} className="mb-4">
                <h4 className="text-md font-semibold text-gray-600 mb-2">{docType}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {files.map((file) => (
                    <div
                      key={file.id} // Use unique ID instead of name+index
                      className="relative bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition transform hover:-translate-y-1"
                    >
                      {/* Remove button in top-right corner */}
                      <button
                        type="button"
                        onClick={() => removeFile(docType, file.id)}
                        disabled={submissionProgress[file.id] !== undefined}
                        className="absolute top-2 right-2 text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed z-10"
                        title="Remove file"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>

                      {/* Main card content */}
                      <div className="flex items-center pr-8"> {/* pr-8 to make space for the X button */}
                        <div className="flex-shrink-0">
                          <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center animate-checkmark">
                            <svg
                              className="w-4 h-4 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="ml-3 w-full">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            Uploaded: {new Date(file.uploadedAt).toLocaleString()}
                          </p>
                          {submissionProgress[file.id] !== undefined && (
                            <div className="mt-2">
                              <progress
                                value={submissionProgress[file.id]}
                                max="100"
                                className="w-full h-2 rounded-full"
                              />
                              <p className="text-xs text-gray-600 mt-1">
                                {Math.round(submissionProgress[file.id])}%
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <style>
        {`
          @keyframes checkmark {
            0% { transform: scale(0); opacity: 0; }
            50% { transform: scale(1.2); opacity: 0.7; }
            100% { transform: scale(1); opacity: 1; }
          }
          .animate-checkmark {
            animation: checkmark 0.3s ease-in-out;
          }
          progress::-webkit-progress-bar {
            background-color: #e5e7eb;
            border-radius: 9999px;
          }
          progress::-webkit-progress-value {
            background-color: #10b981;
            border-radius: 9999px;
          }
          progress::-moz-progress-bar {
            background-color: #10b981;
            border-radius: 9999px;
          }
        `}
      </style>
    </div>
  );
};

export default VehicleInspectionUpload;